import {useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router'
import {useDrag} from 'react-ui-animate'
import {Button} from 'brand/Button'
import {ActionPanel} from 'brand/ActionPanel'
import {ClosePopup} from 'brand/header/ClosePopup'
import {expectationsFor, LookExpectations} from './LookExpectations'

import brush from './icon-brush.svg'
import play from './icon-play.svg'
import aspect from './aspect-ratio.svg'

const addExpectations = looks => Object.entries(looks)
  .map(([k, v]) => ({id: k, ...v, ...expectationsFor(v)}))

export const Looks = ({looksData, selectedLook, setSelectedLook}) => {
  const nav = useNavigate()

  const [looksWithExpectations] = useState(addExpectations(looksData))
  const frontIndex = Math.max(selectedLook
    ? looksWithExpectations.findIndex(it => it.id === selectedLook)
    : 0, 0)
  const leftIndex = (frontIndex + 1) % 3
  const rightIndex = (frontIndex + 2) % 3

  const [positions, setPositions] = useState({
    front: looksWithExpectations[frontIndex].id,
    backLeft: looksWithExpectations[leftIndex].id,
    backRight: looksWithExpectations[rightIndex].id,
  })
  const [showComingSoon, setShowComingSoon] = useState(false)

  const invertedPositions = () => Object.fromEntries(
    Object.entries(positions).map(([k, v]) => [v, k])
  )

  const classesFor = id => {
    const position = invertedPositions()[id]
    return {
      front: 'front',
      backLeft: 'back left',
      backRight: 'back right',
    }[position]
  }

  const setFront = id => {
    setSelectedLook(id)
    const now = invertedPositions()[id]
    if (now === 'front') {
      return
    } else if (now === 'backLeft') {
      // rotate front to right
      setPositions({
        front: positions.backLeft,
        backLeft: positions.backRight,
        backRight: positions.front,
      })
    } else {
      // rotate front to left
      setPositions({
        front: positions.backRight,
        backLeft: positions.front,
        backRight: positions.backLeft,
      })
    }
  }

  const swipeLeft = () => {
    setFront(positions.backRight)
  }
  const swipeRight = () => {
    setFront(positions.backLeft)
  }

  return (
    <>
      <ClosePopup onClose={() => nav('/consult')} />
      <div className="column">
        <div className="heading size-xxxl">Your looks</div>
        <section className="intro">
          <div className="content size-l">
            Personalised looks for you and your needs
          </div>
        </section>
        <section>
          <div className="content">
            <Button action={() => setShowComingSoon(true)}>
              <img src={brush} alt="Brush icon" /> Fine tune
            </Button>
          </div>
        </section>
        <section className="looks-carousel">
          {looksWithExpectations.map(({id, ...lookInfo}) => (
            <Look
              id={id}
              {...lookInfo}
              classes={classesFor(id)}
              isFront={positions.front === id}
              setFront={setFront}
              swipeLeft={swipeLeft}
              swipeRight={swipeRight}
              key={id}
            />
          ))}
        </section>
      </div>
      <ActionPanel title="Coming soon" show={showComingSoon} onClose={() => setShowComingSoon(false)}>
        <div className="inline-column content">
          <img src={aspect} alt="Aspect ratio" />
          <div className="title size-m">Fine tune your looks</div>
          <div className="detail size-m">Dial up the colour or the style and adjust your looks to fit what you need</div>
        </div>
      </ActionPanel>
    </>
  )
}

const swipeThreshold = 48

const Look = ({
  id, name, imgUrl, stepCount, durationMinutes,
  classes,
  isFront, setFront, swipeLeft, swipeRight,
}) => {
  const ref = useRef(null)
  const [dragging, setDragging] = useState(false)
  const draggingClass = dragging ? 'dragging' : ''
  const [transitioning, setTransitioning] = useState(false)

  const debounceClick = () => {
    setTransitioning(true)
    setTimeout(() => setTransitioning(false), 300)
  }

  const bind = useDrag(({down, movementX}) => {
    if (!isFront) {
      return
    }
    if (down && !dragging) { // begin drag
      setDragging(true)
    }
    if (dragging && !down) { // end drag
      if (movementX > swipeThreshold) {
        debounceClick()
        swipeRight()
      } else if (movementX < -swipeThreshold) {
        debounceClick()
        swipeLeft()
      }
      setDragging(false)
    }
    ref.current?.style?.setProperty('--x', `${movementX}px`)
  })

  const maybeOnClick =
    (isFront || transitioning) ? {} : {onClick: () => setFront(id)}

  return (
    <div
      className={`look-tile ${classes} ${draggingClass}`}
      style={{backgroundImage: `url(${imgUrl})`}}
      {...maybeOnClick}
      ref={ref}
    >
      <div {...bind()} className="info column float-cover">
        <div className="title size-xl">{name}</div>
        <LookExpectations {...{stepCount, durationMinutes}} />
        <div style={{flexGrow: 1}} />
        <Link to={`/consult/looks/custom?id=${id}`}>
          <img src={play} alt="Play icon" />
          <div className="title size-ml">View tutorial</div>
        </Link>
      </div>
    </div>
  )
}
